/**
 * Model Line Tool - 3D Line Creation
 * 
 * Creates 3D lines in the model.
 */

import { v4 as uuidv4 } from 'uuid';
import { Element } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

export interface ModelLineGeometryData {
    start: [number, number, number];
    end: [number, number, number];
}

export interface ModelLineElement extends Element {
    category: 'ModelLines';
    geometryData: ModelLineGeometryData;
}

export class ModelLineTool extends BaseTool {
    id = 'model_line';
    name = 'Model Line';
    cursor = 'crosshair';

    private startPoint: THREE.Vector3 | null = null;
    private currentPoint: THREE.Vector3 | null = null;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;

        const snapped = this.snapToGrid(point);

        if (!this.startPoint) {
            this.startPoint = snapped;
            this.currentPoint = snapped;
        } else {
            this.createModelLine(this.startPoint, snapped);

            // Chain
            this.startPoint = snapped;
            this.currentPoint = snapped;
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point) {
            this.currentPoint = this.snapToGrid(point);
            this.updatePreview();
        }
    }

    onPointerUp(): void {}

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            if (this.startPoint) {
                this.startPoint = null;
                this.currentPoint = null;
                if (this.context?.appStore) {
                    this.context.appStore.setToolPreview(null);
                }
            } else {
                super.onKeyDown(event);
            }
        }
    }

    private updatePreview() {
        if (!this.context) return;

        if (this.startPoint && this.currentPoint) {
            this.context.appStore.setToolPreview({
                type: 'line',
                start: this.startPoint,
                end: this.currentPoint
                // No fixed color: the viewport draws line previews with the
                // theme ink (white in dark mode, black in light mode)
            });
        } else {
            this.context.appStore.setToolPreview(null);
        }
    }

    private createModelLine(start: THREE.Vector3, end: THREE.Vector3) {
        if (!this.context) return;

        const geometryData: ModelLineGeometryData = {
            start: [start.x, start.y, start.z],
            end: [end.x, end.y, end.z]
        };

        const line: ModelLineElement = {
            id: uuidv4(),
            category: 'ModelLines',
            type: 'Model Line',
            parameters: {
                'Length': start.distanceTo(end).toFixed(2) + ' m'
            },
            constraints: [],
            geometryData
        };

        this.context.bimStore.addElement(line);
    }
}
